import React, { Component, ReactNode } from "react"
import { useLocation } from "react-router-dom"
import { Fallback } from "./Fallback"

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * 火山APM错误边界组件
 * 捕获React组件错误并上报到火山APM，收集关键信息用于定位问题
 */
class VolcanoErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo,
    })

    // 收集关键上下文信息
    const context = this.collectErrorContext(error, errorInfo)

    // 上报到火山APM
    this.reportToVolcanoAPM(error, errorInfo, context)

    // 打印错误到控制台
    console.error("页面错误:", error.message, error)
  }

  /**
   * 收集关键上下文信息（只保留定位问题必需的）
   */
  collectErrorContext(error: Error, errorInfo: React.ErrorInfo) {
    const timestamp = Date.now()
    const store = (window as any).store
    const state = store?.getState()
    const deviceinfo = state?.env?.deviceinfo
    const env = state?.env

    return {
      // ========== 错误信息 ==========
      error_message: error?.message || "Unknown error",
      error_name: error?.name || "Error",
      error_stack: error?.stack || "",
      component_stack: errorInfo?.componentStack || "",

      // ========== 页面信息 ==========
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,

      // ========== 时间戳 ==========
      timestamp,
      timestamp_iso: new Date(timestamp).toISOString(),

      // ========== 核心用户标识 ==========
      user_id: deviceinfo?.h_m,
      device_id: deviceinfo?.h_did,

      // ========== 设备&应用信息 ==========
      device_type: deviceinfo?.h_dt === 0 ? "Android" : "iOS",
      device_model: deviceinfo?.h_model,
      app_version: deviceinfo?.h_av,
      app_name: deviceinfo?.h_app,
      os_version: deviceinfo?.h_sys,

      // ========== 地理位置（区域） ==========
      area_code: deviceinfo?.h_area_code,
      language: deviceinfo?.h_language,

      // ========== 语音房场景（关键！）==========
      room_id: deviceinfo?.h_web_room_id || null,

      // ========== 网络状态 ==========
      network_type: deviceinfo?.h_nt,
      is_online: navigator.onLine,
      connection: this.getConnectionInfo(),

      // ========== 环境标识 ==========
      is_test: deviceinfo?.h_test,
      channel: deviceinfo?.h_ch,
      client_ip: env?.clientIp,

      // ========== JSBridge 状态 ==========
      jsbridge_available: !!(window as any).HiyaJSBridge,

      // ========== 视口信息（用于判断显示问题）==========
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
    }
  }

  /**
   * 获取网络连接信息
   */
  getConnectionInfo() {
    try {
      const conn =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection

      if (!conn) return null

      return {
        effective_type: conn.effectiveType, // 4g, 3g, 2g
        rtt: conn.rtt, // 网络延迟
        downlink: conn.downlink, // 下行速度
      }
    } catch (e) {
      return null
    }
  }

  /**
   * 上报错误到火山APM
   */
  reportToVolcanoAPM(error: Error, errorInfo: React.ErrorInfo, context: any) {
    try {
      if (typeof window === "undefined") return

      const apmPlus = (window as any).apmPlus
      if (!apmPlus || typeof apmPlus !== "function") {
        return
      }

      // 🎯 标记白屏已被处理，避免重复上报
      ;(window as any).__white_screen_handled__ = true
      ;(window as any).__white_screen_error_time__ = Date.now()

      setTimeout(() => {
        try {
          delete (window as any).__white_screen_handled__
          delete (window as any).__white_screen_error_time__
        } catch (e) {
          // 忽略
        }
      }, 30000)

      // 🔥 上报到火山APM
      apmPlus("error", {
        message: context.error_message,
        stack: context.error_stack,
        name: context.error_name,
        // 所有上下文信息都放在 extra 中
        extra: {
          ...context,
          source: "error_boundary", // 标识来源
        },
      })
    } catch (e) {
      // 忽略上报失败
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <Fallback />
    }

    return this.props.children
  }
}

/**
 * 带 Location 信息的 ErrorBoundary
 */
export const VolcanoErrorBoundary: React.FC<Props> = ({
  children,
  fallback,
}) => {
  const location = useLocation()

  // 存储当前路径信息
  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        "current_path",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          timestamp: Date.now(),
        })
      )
    } catch (e) {
      // 忽略
    }
  }, [location])

  return (
    <VolcanoErrorBoundaryClass fallback={fallback}>
      {children}
    </VolcanoErrorBoundaryClass>
  )
}

export default VolcanoErrorBoundary
